import { Toast } from '@base-ui/react/toast';
import { CloseCircle } from 'iconsax-reactjs';
import { type FC, type ReactNode, useMemo } from 'react';
import { Button } from './Button';
import { cn } from './react';

const ToastList: FC = () => {
  const { toasts } = Toast.useToastManager();

  return toasts.slice(0, 6).map((toast) => (
    <Toast.Root
      key={toast.id}
      toast={toast}
      className={cn(
        'rounded-lg border p-3 select-none',
        'absolute left-0 bottom-0 w-full origin-bottom shadow-2xl',
        '[--gap:1rem]',
        '[--peek:1.5rem]',
        '[--scale:calc(max(0,1-(var(--toast-index)*0.05)))]',
        '[--offset-y:calc(var(--toast-index)*var(--gap)*-1)]',
        'translate-y-[calc(var(--offset-y)-(var(--toast-index)*var(--peek)))]',
        'transition-all duration-300 ease-in-out',
        'data-starting-style:translate-y-full',
        'data-ending-style:opacity-0',
        // themes
        'bg-background-primary border-border-disabled',
        'data-[type=negative]:text-content-negative',
        'data-[type=info]:text-yellow-600 data-[type=info]:border-yellow-500',
      )}
    >
      <Toast.Content className="overflow-hidden">
        <Toast.Title className="text-base font-medium" />
        <Toast.Description className="text-sm mt-1" />
        <Toast.Close
          className="absolute top-0 end-0 p-2"
          aria-label="Close"
          data-close
        >
          <Button
            variant="transparent"
            type="div"
            className="bg-transparent!"
            fab
            size={8}
          >
            <CloseCircle size={20} />
          </Button>
        </Toast.Close>
      </Toast.Content>
    </Toast.Root>
  ));
};

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => (
  <Toast.Provider limit={6}>
    {children}
    <Toast.Portal container={document.getElementById('toast-root')}>
      <Toast.Viewport className="fixed bottom-3 left-1/2 -translate-x-1/2 mx-auto flex flex-col-reverse gap-3 max-w-md w-[calc(100%-1rem)]">
        <ToastList />
      </Toast.Viewport>
    </Toast.Portal>
  </Toast.Provider>
);

export const useToast = () => {
  const toastManager = Toast.useToastManager();
  return useMemo(() => {
    return {
      add: ({
        title,
        message,
        variant,
        timeout = 3_000,
      }: {
        title?: string;
        message?: ReactNode;
        variant?: 'negative' | 'default' | 'info';
        timeout?: number;
      }) =>
        toastManager.add({
          title,
          description: message,
          type:
            variant === 'negative'
              ? 'negative'
              : variant === 'info'
                ? 'info'
                : undefined,
          timeout,
        }),
      remove: toastManager.close,
    };
  }, [toastManager.add, toastManager.close]);
};
